import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { type Component, type ComponentProps, createMemo, For } from "solid-js";
import { fromActorRef } from "@xstate/solid";
import {
  type DiagnosticItem,
  type DiagnosticSection,
  getApiStatusCategory,
  getApiStatusLabelKey,
  getSyncStatusCategory,
  getSyncStatusLabelKey,
} from "@famidex/shared";
import { Info, RefreshCw, Server, UserCircle, Wifi, WifiOff } from "lucide-solid";

import { POWERSYNC_URL, SERVER_URL } from "@/lib/runtime-config";
import { appRoute } from "./route";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { GlassSurface } from "@/components/ui/glass-surface";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/ui/page-container";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { CopyDiagnosticsButton } from "@/components/copy-diagnostics-button";
import { useTheme } from "@/providers/theme-provider";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";

function DiagnosticRow(props: {
  label: string;
  value: string;
  status: "success" | "warning" | "danger" | "neutral";
  icon: Component<ComponentProps<"svg"> & { size?: number | string }>;
}) {
  const getStatusColorClass = () => {
    switch (props.status) {
      case "success": {
        return "text-green-500 bg-green-500/10";
      }
      case "warning": {
        return "text-yellow-500 bg-yellow-500/10";
      }
      case "danger": {
        return "text-red-500 bg-red-500/10";
      }
      case "neutral": {
        return "text-muted-foreground bg-muted/10";
      }
      default: {
        return "text-muted-foreground bg-muted/10";
      }
    }
  };

  const getTextColorClass = () => {
    switch (props.status) {
      case "success": {
        return "text-green-500";
      }
      case "warning": {
        return "text-yellow-500";
      }
      case "danger": {
        return "text-red-500";
      }
      case "neutral": {
        return "text-muted-foreground";
      }
      default: {
        return "text-muted-foreground";
      }
    }
  };

  return (
    <div class="flex items-center justify-between gap-4 py-2">
      <div class="flex items-center gap-3">
        <div
          class={`flex h-8 w-8 items-center justify-center rounded-full ${getStatusColorClass()}`}
        >
          <props.icon size={16} />
        </div>
        <span class="text-sm font-medium">{props.label}</span>
      </div>
      <span class={`text-sm font-bold shrink text-right truncate ${getTextColorClass()}`}>
        {props.value}
      </span>
    </div>
  );
}

/**
 * Lo único que la UI pone y el informe no: el icono. Vive fuera del modelo para
 * que el modelo siga siendo serializable y compartido.
 */
const ICONO_POR_CLAVE: Record<string, typeof Info> = {
  online: Wifi,
  session: UserCircle,
  api: Server,
  sync: RefreshCw,
};

/**
 * `online` conserva el icono partido de este repo (Wifi / WifiOff). Se decide
 * leyendo `raw` del propio modelo, no una segunda fuente: si mirase el contexto
 * por su cuenta volveríamos a tener dos verdades para la misma fila.
 */
function iconoDe(item: DiagnosticItem): typeof Info {
  if (item.key === "online") {
    return item.raw === false ? WifiOff : Wifi;
  }
  return ICONO_POR_CLAVE[item.key] ?? Info;
}

function DebugPage() {
  const { connectivityRef } = usePowerSyncContext();
  const state = fromActorRef(connectivityRef);
  const { t } = useI18n();

  const context = createMemo(() => state().context);
  const stateValue = createMemo(() => state().value);

  const isRetryInProgress = createMemo(
    () => stateValue() === "checking" || stateValue() === "manualChecking",
  );

  const handleForceRetry = () => {
    connectivityRef.send({ type: "RETRY" });
  };

  const getApiLabel = () => t(getApiStatusLabelKey((v) => state().matches(v), context().apiStatus));
  const getApiState = () => getApiStatusCategory((v) => state().matches(v), context().apiStatus);
  const getSyncLabel = () =>
    t(getSyncStatusLabelKey((v) => state().matches(v), context().syncStatus));
  const getSyncState = () => getSyncStatusCategory((v) => state().matches(v), context().syncStatus);

  const canRetry = createMemo(() => state().can({ type: "RETRY" }));

  const theme = useTheme();
  const session = useSession();

  /**
   * FUENTE ÚNICA de la pantalla: la UI pinta esto y el botón copia esto mismo.
   * Antes la pantalla tenía sus filas y el informe su propio objeto — dos
   * fuentes para lo mismo, así que añadir una fila aquí dejaba el informe
   * incompleto sin que nadie se enterara.
   *
   * `key` es estable e independiente del idioma (la lógica lo usa), `label` y
   * `value` van ya traducidos, `status` solo lo mira la UI y `raw` lo mira la
   * lógica para no depender de una traducción.
   */
  /** Id de usuario recortado — nunca entero: el informe se pega en chats. */
  const idRecortado = () => {
    const id = session.userId();
    return id ? `${id.slice(0, 8)}…` : "-";
  };

  // Secciones NOMBRADAS, no indexadas: `secciones()[1]` acoplaba la pantalla al
  // orden del array, y basta con insertar una para romperlo en silencio.
  const conectividad = (): DiagnosticSection => ({
    title: t(msg`Connectivity`),
    items: [
      {
        key: "online",
        label: t(msg`Internet Access`),
        value: context().isOnline ? t(msg`Connected`) : t(msg`Disconnected`),
        status: context().isOnline ? "success" : "danger",
        raw: context().isOnline,
      },
      {
        key: "session",
        label: t(msg`User Session`),
        value: context().isAuthenticated ? t(msg`Active`) : t(msg`None`),
        status: context().isAuthenticated ? "success" : "warning",
        raw: context().isAuthenticated,
      },
      {
        key: "api",
        label: t(msg`API Server (Writes)`),
        value: getApiLabel(),
        status: getApiState(),
        raw: context().apiStatus,
      },
      {
        key: "clientVersion",
        label: t(msg`Client Version`),
        value: `${import.meta.env.APP_VERSION} (${import.meta.env.COMMIT_HASH})`,
        status: "neutral",
      },
      ...(context().serverVersion
        ? [
            {
              key: "serverVersion",
              label: t(msg`Server Version`),
              value: String(context().serverVersion),
              status: "neutral" as const,
            },
          ]
        : []),
      {
        key: "sync",
        label: t(msg`PowerSync (Sync)`),
        value: getSyncLabel(),
        status: getSyncState(),
        // `raw` lleva el estado agregado; el CRUDO de la máquina se omite como
        // fila porque su etiqueta ("Status") no está traducida en ningún
        // catálogo y añadirla la mostraría en inglés en los once idiomas. Es
        // el mismo criterio que se aplicó al resto de cadenas. Pendiente de la
        // pasada de i18n.
        raw: context().syncStatus,
      },
    ],
  });

  const sistema = (): DiagnosticSection => ({
    title: t(msg`System Information`),
    items: [
      // eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): technical value, not user-facing text
      { key: "adapter", label: t(msg`Adapter`), value: "WASM + OPFS" },
      { key: "serverUrl", label: t(msg`Server URL`), value: SERVER_URL },
      { key: "powersyncUrl", label: t(msg`PowerSync URL`), value: POWERSYNC_URL },
      {
        key: "mode",
        label: t(msg`Mode`),
        value: import.meta.env.DEV ? "development" : "production",
      },
      {
        key: "colorMode",
        // El canon parte esto en dos filas (`Appearance` y `Theme`), pero
        // `Theme` no está en ninguno de los once catálogos de este repo y
        // añadirla la dejaría en inglés en todos sin que el gate se queje. Va
        // en una sola fila con etiqueta ya traducida: el dato que importa —la
        // divergencia entre modo efectivo y el del dispositivo, origen de los
        // fallos de "texto en el modo contrario"— sigue estando entero.
        label: t(msg`Appearance`),
        // eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): technical value, not user-facing text
        value: `${theme.colorMode()} → ${theme.tokens()?.id ?? "-"} (${theme.isDark() ? "dark" : "light"} / device ${theme.systemIsDark() ? "dark" : "light"})`,
      },
      {
        key: "account",
        // `Account` sí está en los once catálogos de este repo (a diferencia de
        // sus gemelos), así que se usa la etiqueta del canon.
        label: t(msg`Account`),
        // Recortado: cruzable con los logs del servidor, pero pegable en un chat.
        value: idRecortado(),
      },
    ],
  });

  /** Lo que se pinta Y lo que se copia. Un solo sitio. */
  const secciones = (): DiagnosticSection[] => [conectividad(), sistema()];

  return (
    <div class="flex flex-1 flex-col overflow-hidden">
      <PageSubHeader title={t(msg`Diagnostics`)} />

      <PageContainer maxWidth="lg" class="space-y-6" containerClass="min-h-0">
        {/* Entrance animation is provided once at the route Outlet
            (app-layout-content). This inner wrapper only handles spacing. */}
        <div class="space-y-6">
          <GlassSurface class="p-4">
            <h2 class="mb-4 text-lg font-semibold">{conectividad().title}</h2>
            <div class="space-y-1">
              {/* Se pinta el MISMO modelo que copia el botón. Añadir una fila
                  arriba la hace aparecer aquí y en el informe a la vez. */}
              <For each={conectividad().items}>
                {(item) => (
                  <DiagnosticRow
                    label={item.label}
                    value={item.value}
                    status={item.status ?? "neutral"}
                    icon={iconoDe(item)}
                  />
                )}
              </For>
            </div>

            <Button
              variant="default"
              class="mt-6 w-full gap-2"
              onClick={handleForceRetry}
              disabled={isRetryInProgress() || !canRetry()}
            >
              <RefreshCw size={18} class={isRetryInProgress() ? "animate-spin" : ""} />
              {t(msg`Force connection retry`)}
            </Button>

            {/* Copiar el diagnóstico entero. Describir a mano lo de esta pantalla
                pierde la mitad de los datos, y el caso que originó esto —la app
                "sincronizando" para siempre porque el servicio estaba caído— se
                resuelve en segundos con el informe delante. */}
            <div class="mt-3">
              <CopyDiagnosticsButton appName="famidex" secciones={secciones} />
            </div>
          </GlassSurface>

          <Card>
            <CardHeader>
              <CardTitle>{sistema().title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div class="space-y-3">
                <For each={sistema().items}>
                  {(item) => (
                    <>
                      <div class="flex items-center justify-between text-sm">
                        <span class="text-muted-foreground">{item.label}</span>
                        <Badge variant="outline" class="font-mono text-xs">
                          {item.value}
                        </Badge>
                      </div>
                      <Separator />
                    </>
                  )}
                </For>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    </div>
  );
}

export const debugRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/debug",
  component: DebugPage,
});
